import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { DEFAULT_EVENT_IMAGE } from 'cb-unified';

interface VideoPlayerProps {
  src: string;
  autoPlay?: boolean;
  controls?: boolean;
  width?: string | number;
  height?: string | number;
  thumbnail?: string;
  onProgressChange?: (percentage: number, currentTime: number, duration: number) => void;
  onPlayClick?: () => boolean | Promise<boolean>;
  maxPlaybackSeconds?: number;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  autoPlay = false, // updated to false so user can click play
  controls = true,
  width = '100%',
  height = 'auto',
  thumbnail = DEFAULT_EVENT_IMAGE,
  onProgressChange,
  onPlayClick,
  maxPlaybackSeconds,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const previewLimitReachedRef = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(src);
      hls.attachMedia(video);

      hls.on(Hls.Events.ERROR, function (event, data) {
        console.error('HLS error:', data);
      });

      return () => {
        hls.destroy();
      };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
    } else {
      console.error('This browser does not support HLS playback.');
    }
  }, [src]);

  useEffect(() => {
    previewLimitReachedRef.current = false;
  }, [maxPlaybackSeconds, src]);

  const handlePlay = async () => {
    if (onPlayClick) {
      const shouldContinue = await onPlayClick();
      if (!shouldContinue) {
        return;
      }
    }

    if (videoRef.current) {
      await videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleVideoPlay = () => setIsPlaying(true);
  const handleVideoPause = () => setIsPlaying(false);

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;

      if (duration > 0) {
        const percentage = (currentTime / duration) * 100;
        // Call the callback if provided
        if (onProgressChange) {
          onProgressChange(percentage, currentTime, duration);
        }
      }

      if (
        typeof maxPlaybackSeconds === 'number' &&
        maxPlaybackSeconds > 0 &&
        currentTime >= maxPlaybackSeconds &&
        !previewLimitReachedRef.current
      ) {
        previewLimitReachedRef.current = true;
        videoRef.current.pause();
        videoRef.current.currentTime = maxPlaybackSeconds;
        setIsPlaying(false);
      }
    }
  };

  return (
    <div className="rounded-xl overflow-hidden" style={{ position: 'relative', width, height }}>
      <video
        className="w-auto h-full object-fill"
        ref={videoRef}
        controls={controls}
        autoPlay={autoPlay}
        poster={thumbnail}
        style={{ objectFit: 'cover' }}
        onPlay={handleVideoPlay}
        onPause={handleVideoPause}
        onTimeUpdate={handleTimeUpdate}
      />
      {!isPlaying && (
        <div
          onClick={handlePlay}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.4)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <svg width="80" height="80" viewBox="0 0 64 64" fill="#fff" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 12 L52 32 L16 52 Z" />
          </svg>
        </div>
      )}
    </div>
  );
};
